// Lightweight GA4 event helper. Safely no-ops when gtag is unavailable
// (local dev, ad-blockers, or before the GA snippet in index.html loads),
// so analytics never breaks the form.
import { getPrintAd, getDigitalAd } from './pricing';

export function trackEvent(name, params = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

// Test orders (Matt's live and sandbox checks) must not count as conversions
// or revenue. A name counts as a test only when "test", "testing" or "sandbox"
// is a whole WORD in it, after splitting run-together names like
// "MattsLiveTestOctober" or "PaidTest2" into words. So "Ron Artest",
// "Fastest Plumbing" or "The Contest Co." are real orders. Also a test: the
// page opened with ?test, or PayPal in sandbox mode ('sb').
const TEST_WORDS = new Set(['test', 'tests', 'testing', 'sandbox']);

export function nameLooksLikeTest(name) {
  const words = String(name || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')        // LiveTest -> Live Test
    .replace(/([A-Za-z])([0-9])/g, '$1 $2')     // Test2 -> Test 2
    .replace(/([0-9])([A-Za-z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/);
  return words.some(w => TEST_WORDS.has(w));
}

export function isTestOrder(form, paypalClientId, search) {
  if (nameLooksLikeTest(form?.contact?.businessName) || nameLooksLikeTest(form?.contact?.name)) return true;
  if (paypalClientId === 'sb') return true;
  const qs = search !== undefined ? search : (typeof window !== 'undefined' ? window.location.search : '');
  return /[?&]test\b/i.test(qs || '');
}

// GA4 ecommerce items for one ad order: one line per placement at its weekly
// price, quantity = weeks it actually runs. Order-wide discounts (multi-week,
// promo) are reported once on the event as `discount`.
export function purchaseItems(form, pricing) {
  const items = [];
  const weeks = pricing?.weeks || form?.weeks || 1;
  if (pricing?.printCost > 0 && form?.printAd) {
    const ad = getPrintAd(form.printAd);
    items.push({
      item_id: `print_${form.printAd}`,
      item_name: ad?.label || String(form.printAd),
      item_category: 'Print',
      price: pricing.printWeekly,
      quantity: weeks,
    });
  }
  (pricing?.digitalLines || []).forEach(line => {
    if (line.weeks <= 0) return;
    const ad = getDigitalAd(line.id);
    items.push({
      item_id: `digital_${line.id}`,
      item_name: ad?.label || String(line.id),
      item_category: 'Digital',
      price: line.unit,
      quantity: line.weeks,
    });
  });
  return items;
}

// Paid order (PayPal captured): `purchase` carries the revenue into GA4's
// Monetization reports; `form_submit` keeps the existing conversion count.
// Test orders send only `ad_test_order`, so they never inflate either.
export function trackPaidOrder(form, data, paypalClientId) {
  const pricing = data?.pricing || {};
  const transactionId = data?.details?.id;
  const promoCode = pricing.promoApplies ? form.promo : null;
  if (isTestOrder(form, paypalClientId)) {
    trackEvent('ad_test_order', { transaction_id: transactionId, value: pricing.total, currency: 'USD' });
    return 'test';
  }
  trackEvent('form_submit', {
    medium:         form.medium,
    org_type:       form.orgType,
    weeks:          form.weeks,
    value:          pricing.total,
    currency:       'USD',
    transaction_id: transactionId,
    // Only report the code when it actually discounted the order. Prefixed ad_*
    // because the main site shares this GA4 property.
    ad_promo_code:    promoCode,
    ad_promo_savings: pricing.promoSavings || 0,
  });
  trackEvent('purchase', {
    transaction_id: transactionId,
    value:          pricing.total,
    currency:       'USD',
    affiliation:    'Lightning ad order form',
    coupon:         promoCode || undefined,
    discount:       (pricing.totalSavings || 0) + (pricing.promoSavings || 0),
    items:          purchaseItems(form, pricing),
  });
  return 'paid';
}
